import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { createSelector } from 'reselect'
import config from 'constants/config'
import { PAYMENT_RESTRICTED_CURRENCIES_FOR_SPOOFED } from 'constants/payment'
import { isStripePaymentElementCardEnabled } from 'lib/payment/stripeUtils'
import { isMerchantFeeEnabled } from 'selectors/featuresSelectors'

const excludedMerchantFeeItems = ['subscription', 'service_fee']
const excludedSubtotalItems = ['service_fee']

export const isSecureSplitPaymentAvailable = createSelector(
  (state: App.State) => state.securePayment.data,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.auth.account.isSpoofed,
  () => config.SPLIT_PAYMENTS_ENABLED,
  (data, regionCode, isSpoofed, splitPaymentsEnabled): boolean => !!data &&
    splitPaymentsEnabled && !!data.orderId &&
    data.splitPayment?.eligible === true &&
    !!isStripePaymentElementCardEnabled(regionCode, data.currency) &&
    !(isSpoofed && PAYMENT_RESTRICTED_CURRENCIES_FOR_SPOOFED.includes(data.currency)),
)

export const areSecureSplitMerchantFeesEnabled = (state: App.State): boolean =>
  isMerchantFeeEnabled(state) && !areCreditPurchaseMerchantFeesDisabled(state)

export const isSecurePaymentPage = createSelector(
  (state: App.State) => state.router.location.pathname,
  (pathname): boolean => !!matchLocaleAwarePath(pathname, { path: '/:regionCode/pay/:paymentLinkId', exact: true }),
)

export const areCreditPurchaseMerchantFeesDisabled = (state: App.State): boolean =>
  state.securePayment.data?.metadata?.disableCreditPurchaseMerchantFees === true

const calculateAmountEligibleForMerchantFee = createSelector(
  (state: App.State) => state.securePayment.data?.items,
  (state: App.State) => state.securePayment.data?.amount,
  (items, amount): number => {
    if (!items || !amount) return 0
    return amount - sum(
      items
        .filter(item => excludedMerchantFeeItems.includes(item.type))
        .map(item => item.total ?? 0),
    )
  },
)

export const calculateSecurePaymentMerchantFeeAmount = createSelector(
  (state: App.State) => state.securePayment.splitPayment,
  (state: App.State) => areCreditPurchaseMerchantFeesDisabled(state),
  (state: App.State) => state.system.merchantFeeConfigs,
  (state: App.State) => state.securePayment.merchantFeePaymentType,
  (state: App.State) => calculateAmountEligibleForMerchantFee(state),
  (splitPayment, disableCreditPurchaseMerchantFees, merchantFeeConfigs, merchantFeePaymentType, payableAmount): number => {
    if (splitPayment.isActive) return splitPayment.merchantFeeAmount ?? 0
    if (disableCreditPurchaseMerchantFees) return 0
    if (!merchantFeePaymentType || merchantFeeConfigs.length === 0 || !payableAmount) return 0

    const merchantFeePercentage = merchantFeeConfigs.find((config) => config.id === merchantFeePaymentType)?.percentage
    if (!merchantFeePercentage) return 0

    const merchantFee = floatify(payableAmount * (merchantFeePercentage / 100))
    return merchantFee
  },
)

export const calculateSecurePaymentSubTotal = createSelector(
  (state: App.State) => state.securePayment.data?.items,
  (items): number => {
    if (!items) return 0
    return sum(
      items
        .filter(item => !excludedSubtotalItems.includes(item.type))
        .map(item => item.total ?? 0))
  },
)

export const calculateSecurePaymentDueNow = createSelector(
  (state: App.State) => calculateSecurePaymentMerchantFeeAmount(state),
  (state: App.State) => state.securePayment.data?.amount,
  (merchantFeeAmount, amount): number | undefined => {
    if (amount === undefined) return undefined
    return merchantFeeAmount + amount
  },
)

export const calculateSecurePaymentGrandTotal = createSelector(
  (state: App.State) => calculateSecurePaymentMerchantFeeAmount(state),
  (state: App.State) => state.securePayment.data?.balanceAmount,
  (state: App.State) => state.securePayment.data?.amount,
  (merchantFeeAmount, balanceAmount, amount): number | undefined => {
    if (amount === undefined) return undefined
    if (balanceAmount) return amount + merchantFeeAmount + balanceAmount
    return merchantFeeAmount + amount
  },
)

export const getDueNowInCents = createSelector(
  (state: App.State) => calculateSecurePaymentDueNow(state),
  (total): number | undefined => {
    if (total === undefined) return undefined
    return Math.round((total + Number.EPSILON) * 100)
  },
)

export const getProductType = createSelector(
  (state: App.State) => state.securePayment.data?.items,
  (items): string => {
    if (!items) return ''
    if (items.some(item => item.type === 'experience')) {
      return 'experience'
    } else if (items.some(item => item.type === 'insurance')) {
      return 'insurance'
    } else if (items.some(item => item.type === 'accommodation')) {
      return 'accommodation'
    } else if (items.some(item => item.type === 'flight')) {
      return 'flight'
    } else if (items.some(item => item.type === 'car_hire')) {
      return 'car-hire'
    } else if (items.some(item => item.type === 'subscription')) {
      return 'lux-plus'
    } else if (items.some(item => item.type === 'cruise')) {
      return 'cruise'
    } else if (items.some(item => (item.type === 'tourV2' || item.type === 'tour'))) {
      return 'tour'
    }
    return ''
  },
)

export const getServiceFee = createSelector(
  (state: App.State) => state.securePayment.data?.items,
  (items): number => {
    if (!items) return 0
    return sum(items.filter(item => item.type === 'service_fee').map(item => item.total ?? 0))
  },
)
